import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import type { Recommendation } from '../../types'

interface OptimizationMapProps {
  recommendations: Recommendation[]
}

export function OptimizationMap({ recommendations }: OptimizationMapProps) {
  if (recommendations.length === 0) {
    return (
      <Card className="bg-card border border-border rounded-2xl p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-2">
          Mapa de Otimização
        </p>
        <p className="text-xs text-muted-foreground/60">Nenhuma ação prioritária identificada.</p>
      </Card>
    )
  }

  return (
    <Card className="bg-card border border-border rounded-2xl p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-3">
        Mapa de Otimização
      </p>
      <div className="space-y-2">
        {recommendations.map((rec, i) => (
          <details key={i} open className="group">
            <summary className="flex items-start gap-3 p-2.5 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors duration-150 cursor-pointer list-none">
              <span
                className={cn(
                  'flex-shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold mt-0.5',
                  rec.status === 'danger' && 'bg-destructive/15 text-destructive',
                  rec.status === 'warning' && 'bg-warning/15 text-warning',
                  rec.status === 'success' && 'bg-success/15 text-success',
                )}
              >
                {rec.status === 'danger' ? 'alta' : rec.status === 'warning' ? 'média' : 'baixa'}
              </span>
              <p className="text-xs text-foreground/80 flex-1 font-medium">{rec.metric}</p>
            </summary>
            <div className="mt-3 space-y-3 px-2 pb-2">
              <div className="grid gap-3 md:grid-cols-2">
                {rec.causes.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Causas</p>
                    <ul className="space-y-0.5">
                      {rec.causes.map((c, j) => (
                        <li key={j} className="text-xs text-muted-foreground flex gap-1.5">
                          <span className="flex-shrink-0 text-muted-foreground/40">·</span>
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {rec.actions.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Ações</p>
                    <ul className="space-y-0.5">
                      {rec.actions.map((a, j) => (
                        <li key={j} className="text-xs text-muted-foreground flex gap-1.5">
                          <span className="flex-shrink-0 text-primary/60">→</span>
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {rec.detail && (
                <p className="text-xs text-muted-foreground/70 leading-relaxed border-l-2 border-border pl-2">
                  {rec.detail}
                </p>
              )}
            </div>
          </details>
        ))}
      </div>
    </Card>
  )
}
